import React from 'react';
import {
    DecoratorNode,
    type EditorConfig,
    type NodeKey,
    type SerializedLexicalNode,
    type Spread,
} from 'lexical';
import { FractionView, ExponentView, RootView, OverlineView } from './MathViews';
import type { SerializedSubstate } from '../../utils/mathTypes';

const EMPTY_SUBSTATE: SerializedSubstate = {
    state: '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}',
};

export type SerializedFractionNode = Spread<
    {
        type: 'fraction';
        version: 1;
        num: SerializedSubstate;
        den: SerializedSubstate;
    },
    SerializedLexicalNode
>;

export type SerializedExponentNode = Spread<
    {
        type: 'exponent';
        version: 1;
        base: SerializedSubstate;
        exp: SerializedSubstate;
    },
    SerializedLexicalNode
>;

export type SerializedRootNode = Spread<
    {
        type: 'root';
        version: 1;
        radicand: SerializedSubstate;
        index: SerializedSubstate | null; // null = square root
    },
    SerializedLexicalNode
>;

export type OverlineVariant = 'segment' | 'line' | 'ray';

export type SerializedOverlineNode = Spread<
    {
        type: 'overline';
        version: 1;
        content: SerializedSubstate;
        variant: OverlineVariant;
    },
    SerializedLexicalNode
>;

/* ===================== Fraction ===================== */

export class FractionNode extends DecoratorNode<React.JSX.Element> {
    __num: SerializedSubstate;
    __den: SerializedSubstate;

    static getType(): string {
        return 'fraction';
    }

    static clone(node: FractionNode): FractionNode {
        return new FractionNode(node.__num, node.__den, node.__key);
    }

    constructor(
        num: SerializedSubstate = EMPTY_SUBSTATE,
        den: SerializedSubstate = EMPTY_SUBSTATE,
        key?: NodeKey
    ) {
        super(key);
        this.__num = num;
        this.__den = den;
    }

    static importJSON(json: SerializedFractionNode): FractionNode {
        return new FractionNode(json.num ?? EMPTY_SUBSTATE, json.den ?? EMPTY_SUBSTATE);
    }

    exportJSON(): SerializedFractionNode {
        return {
            type: 'fraction',
            version: 1,
            num: this.__num,
            den: this.__den,
        };
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const span = document.createElement('span');
        span.className = 'pm-math pm-math-frac';
        span.setAttribute('data-lexical-inline', 'true');
        return span;
    }

    updateDOM(): boolean {
        return false;
    }

    isInline(): boolean {
        return true;
    }
    isIsolated(): boolean {
        return false;
    }

    decorate(): React.JSX.Element {
        return <FractionView nodeKey={this.getKey()} num={this.__num} den={this.__den} />;
    }
}

/* ===================== Exponent ===================== */

export class ExponentNode extends DecoratorNode<React.JSX.Element> {
    __base: SerializedSubstate;
    __exp: SerializedSubstate;

    static getType(): string {
        return 'exponent';
    }

    static clone(node: ExponentNode): ExponentNode {
        return new ExponentNode(node.__base, node.__exp, node.__key);
    }

    constructor(
        base: SerializedSubstate = EMPTY_SUBSTATE,
        exp: SerializedSubstate = EMPTY_SUBSTATE,
        key?: NodeKey
    ) {
        super(key);
        this.__base = base;
        this.__exp = exp;
    }

    static importJSON(json: SerializedExponentNode): ExponentNode {
        return new ExponentNode(json.base ?? EMPTY_SUBSTATE, json.exp ?? EMPTY_SUBSTATE);
    }

    exportJSON(): SerializedExponentNode {
        return {
            type: 'exponent',
            version: 1,
            base: this.__base,
            exp: this.__exp,
        };
    }

    createDOM(): HTMLElement {
        const span = document.createElement('span');
        span.className = 'pm-math pm-math-exp';
        span.setAttribute('data-lexical-inline', 'true');
        return span;
    }

    updateDOM(): boolean {
        return false;
    }

    isInline(): boolean {
        return true;
    }
    isIsolated(): boolean {
        return false;
    }

    decorate(): React.JSX.Element {
        return <ExponentView nodeKey={this.getKey()} base={this.__base} exp={this.__exp} />;
    }
}

/* ===================== Root ===================== */

export class RootNode extends DecoratorNode<React.JSX.Element> {
    __radicand: SerializedSubstate;
    __index: SerializedSubstate | null;

    static getType(): string {
        return 'root';
    }

    static clone(node: RootNode): RootNode {
        return new RootNode(node.__radicand, node.__index, node.__key);
    }

    constructor(
        radicand: SerializedSubstate = EMPTY_SUBSTATE,
        index: SerializedSubstate | null = null,
        key?: NodeKey
    ) {
        super(key);
        this.__radicand = radicand;
        this.__index = index;
    }

    static importJSON(json: SerializedRootNode): RootNode {
        return new RootNode(json.radicand ?? EMPTY_SUBSTATE, json.index ?? null);
    }

    exportJSON(): SerializedRootNode {
        return {
            type: 'root',
            version: 1,
            radicand: this.__radicand,
            index: this.__index,
        };
    }

    createDOM(): HTMLElement {
        const span = document.createElement('span');
        span.className = 'pm-math pm-math-root';
        span.setAttribute('data-lexical-inline', 'true');
        return span;
    }

    updateDOM(): boolean {
        return false;
    }

    isInline(): boolean {
        return true;
    }
    isIsolated(): boolean {
        return false;
    }

    decorate(): React.JSX.Element {
        return <RootView nodeKey={this.getKey()} radicand={this.__radicand} index={this.__index} />;
    }
}

/* ===================== Overline Node ===================== */

export class OverlineNode extends DecoratorNode<React.JSX.Element> {
    __content: SerializedSubstate;
    __variant: OverlineVariant;

    static getType(): string {
        return 'overline';
    }

    static clone(node: OverlineNode): OverlineNode {
        return new OverlineNode(node.__content, node.__variant, node.__key);
    }

    constructor(
        content: SerializedSubstate = EMPTY_SUBSTATE,
        variant: OverlineVariant = 'segment',
        key?: NodeKey
    ) {
        super(key);
        this.__content = content;
        this.__variant = variant;
    }

    static importJSON(json: SerializedOverlineNode): OverlineNode {
        return new OverlineNode(json.content ?? EMPTY_SUBSTATE, json.variant ?? 'segment');
    }

    exportJSON(): SerializedOverlineNode {
        return {
            type: 'overline',
            version: 1,
            content: this.__content,
            variant: this.__variant,
        };
    }

    createDOM(_config: EditorConfig): HTMLElement {
        const span = document.createElement('span');
        span.className = 'pm-math pm-math-overline';
        span.setAttribute('data-lexical-inline', 'true');
        span.setAttribute('data-overline-variant', this.__variant);
        return span;
    }

    updateDOM(prevNode: OverlineNode, dom: HTMLElement): boolean {
        // Update variant if it changed
        if (prevNode.__variant !== this.__variant) {
            dom.setAttribute('data-overline-variant', this.__variant);
        }
        return false;
    }

    isInline(): boolean {
        return true;
    }
    isIsolated(): boolean {
        return false;
    }

    decorate(): React.JSX.Element {
        return (
            <OverlineView
                nodeKey={this.getKey()}
                content={this.__content}
                variant={this.__variant}
            />
        );
    }
}

/* Helpers to construct nodes */
export function $createFractionNode(): FractionNode {
    return new FractionNode();
}
export function $createExponentNode(): ExponentNode {
    return new ExponentNode();
}
export function $createRootNode(withIndex: boolean): RootNode {
    return new RootNode(undefined, withIndex ? EMPTY_SUBSTATE : null);
}
export function $createOverlineNode(variant: OverlineVariant = 'segment'): OverlineNode {
    return new OverlineNode(undefined, variant);
}
